import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  BarChart3, 
  Settings as SettingsIcon,
  LogOut,
  Gamepad2,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BookOpen,
  HelpCircle,
  ShoppingBag,
  Smartphone
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen, isCollapsed, onToggleCollapse, onLogout, user, theme, toggleTheme, hiddenItems = [] }) => {
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar-expanded-sections');
      return saved ? JSON.parse(saved) : {
        'Основне': true,
        'Навчання': true,
        'Конструктори': false,
        'Симулятори': false,
        'Адміністрування': false
      };
    } catch (e) {
      return {
        'Основне': true,
        'Навчання': true,
        'Конструктори': false,
        'Симулятори': false,
        'Адміністрування': false
      };
    }
  });

  const toggleSection = (title) => {
    setExpandedSections(prev => {
      const next = { ...prev, [title]: !prev[title] };
      localStorage.setItem('sidebar-expanded-sections', JSON.stringify(next));
      return next;
    });
  };

  const menuGroups = [
    {
      title: 'Основне',
      items: [
        { id: 'dashboard', label: 'Панель', icon: <LayoutDashboard size={20} />, roles: ['superadmin', 'admin', 'trainer', 'viewer'] },
        { id: 'students', label: 'Гравці', icon: <Users size={20} />, roles: ['superadmin', 'admin', 'trainer'] },
        { id: 'courses', label: 'Навчання', icon: <BookOpen size={20} />, roles: ['superadmin', 'admin', 'trainer'] },
        { id: 'store', label: 'Магазин', icon: <ShoppingBag size={20} />, roles: ['superadmin', 'admin', 'trainer'] },
        { id: 'guide', label: 'Як користуватися', icon: <HelpCircle size={20} />, roles: ['superadmin', 'admin', 'trainer', 'viewer'] },
        { id: 'analytics', label: 'Аналітика', icon: <BarChart3 size={20} />, roles: ['localadmin', 'superadmin', 'admin', 'trainer'] },
        { id: 'logs', label: 'Логи моб. додатку', icon: <Smartphone size={20} />, roles: [], usernames: ['kultup'] },
      ]
    }
  ];

  const filterByRole = (items) => items.filter(item => {
    const roleAllowed = !item.roles || item.roles.includes(user?.role);
    const usernameAllowed = !item.usernames || (user?.username && item.usernames.includes(user.username));
    const notHidden = !hiddenItems.includes(item.id);
    return (roleAllowed || usernameAllowed) && notHidden;
  });

  const renderNavItem = (item) => (
    <div
      key={item.id}
      className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
      title={isCollapsed ? item.label : ''}
      onClick={() => {
        if (item.url) {
          window.open(item.url, '_blank');
        } else {
          setActiveTab(item.id);
        }
        if (window.innerWidth <= 768) setIsOpen(false);
      }}
    >
      <span className="nav-icon">{item.icon}</span>
      {!isCollapsed && (
        <div className="nav-item-content">
          {item.isDevelopment && <span className="dev-label">в розробці</span>}
          <span className="nav-label">{item.label}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        {user?.platform === 'funadmin'
          ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '1.5rem', fontWeight: 700, color: '#fff', padding: '0.5rem 0' }}>
              <Gamepad2 size={28} color="#38bdf8" />
              {!isCollapsed && <span>FunAdmin</span>}
            </div>
          : <img src="/km-logo.png" alt="Країна Мрій" className="sidebar-logo-img" />
        }
        <button className="mobile-close" onClick={() => setIsOpen(false)}>×</button>
      </div>
      
      <nav className="sidebar-nav">
        {menuGroups.map((group) => {
          const visible = filterByRole(group.items);
          if (visible.length === 0) return null;
          const isExpanded = expandedSections[group.title] !== false;

          return (
            <div key={group.title} className={`nav-group ${isExpanded ? 'expanded' : 'collapsed'}`}>
              {!isCollapsed ? (
                <div 
                  className="nav-section-title" 
                  onClick={() => toggleSection(group.title)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
                >
                  <span>{group.title}</span>
                  <ChevronDown size={14} style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                </div>
              ) : (
                <div className="nav-section-divider" />
              )}
              {(!isCollapsed ? isExpanded : true) && visible.map(renderNavItem)}
            </div>
          );
        })}
      </nav>
      
      <div className="sidebar-footer">
        <button 
          type="button"
          className="btn-collapse-toggle" 
          onClick={onToggleCollapse}
          title={isCollapsed ? "Розгорнути меню" : "Згорнути меню"}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!isCollapsed && <span style={{ marginLeft: '8px' }}>Згорнути меню</span>}
        </button>

        <div className="user-profile">
          <div className="user-avatar">{user?.username?.substring(0, 2).toUpperCase() || '??'}</div>
          {!isCollapsed && (
            <div className="user-info">
              <span className="user-name">{user?.username || 'Гість'}</span>
              <span className="user-role">{user?.role || 'user'}</span>
            </div>
          )}
        </div>
        <div className="sidebar-footer-actions">
          {['superadmin', 'admin'].includes(user?.role) && (
            <button 
              type="button"
              className={`btn-admin-toggle ${activeTab === 'admin' ? 'active' : ''}`} 
              onClick={() => setActiveTab('admin')} 
              title="Адміністрування"
            >
              <SettingsIcon size={18} />
            </button>
          )}
          <button className="btn-theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Світла тема' : 'Темна тема'}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button 
            type="button"
            className="btn-logout" 
            onClick={onLogout} 
            title="Вийти"
          >
            <LogOut size={16} />
            {!isCollapsed && 'Вийти'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
